import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug],
  );

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: activeCategory.name },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 奶水动画`,
    description: activeCategory.description,
    url: `https://naishuidonghua.cn/category/${activeSlug}`,
    inLanguage: 'zh-Hans',
    isPartOf: {
      '@type': 'WebSite',
      name: '奶水动画',
      url: 'https://naishuidonghua.cn/',
    },
  };

  const categoryIcons: Record<string, string> = {
    'milk-secretion': 'ri-drop-fill',
    'feeding-positions': 'ri-heart-pulse-fill',
    'milk-boosting': 'ri-arrow-up-circle-fill',
    'weaning': 'ri-emotion-happy-fill',
  };

  const categoryBgColors: Record<string, string> = {
    'milk-secretion': 'bg-accent-100',
    'feeding-positions': 'bg-primary-100',
    'milk-boosting': 'bg-secondary-100',
    'weaning': 'bg-accent-100',
  };

  const categoryTextColors: Record<string, string> = {
    'milk-secretion': 'text-accent-700',
    'feeding-positions': 'text-primary-700',
    'milk-boosting': 'text-secondary-700',
    'weaning': 'text-accent-700',
  };

  const categoryTabBgColors: Record<string, string> = {
    'milk-secretion': 'bg-accent-500',
    'feeding-positions': 'bg-primary-500',
    'milk-boosting': 'bg-secondary-500',
    'weaning': 'bg-accent-500',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-24 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
              {activeCategory.name}
            </h1>
            <p className="text-sm text-foreground-500">{activeCategory.description}</p>
          </div>

          {/* Category Tabs */}
          <nav className="flex flex-wrap gap-2 mb-10" aria-label="文章分类">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? `${categoryTabBgColors[cat.slug]} text-background-50`
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${categoryIcons[cat.slug]} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </nav>

          {/* Article List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="h-44 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    title={article.title}
                  />
                </div>
                <div className="p-4 md:p-5">
                  <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${categoryBgColors[activeCategory.slug]} ${categoryTextColors[activeCategory.slug]} mb-2 whitespace-nowrap`}>
                    {article.categoryName}
                  </span>
                  <h3 className="font-heading text-sm md:text-base text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>{article.readTime} 分钟
                    </span>
                    <span>{article.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block"></i>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}